import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Button,
  Card,
  Popconfirm,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, TruckOutlined } from "@ant-design/icons";
import { ShippingRate, ShippingTypeOption } from "./types";
import {
  deleteShippingRate,
  listShippingRates,
  listShippingTypes,
  updateShippingRate,
} from "./api";
import { RateFormDrawer } from "./RateFormDrawer";
import { DeliveryConfigCard } from "./DeliveryConfigCard";
import { StorePickupCard } from "./StorePickupCard";
import { ZippinCard } from "./ZippinCard";

const { Title } = Typography;

export const ShippingConfigList: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const sellerCode = decodeURIComponent(id ?? "");

  const [rates, setRates] = useState<ShippingRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<ShippingRate | null>(null);
  const [shippingTypes, setShippingTypes] = useState<ShippingTypeOption[]>([]);

  const shippingTypeLabel = (id: number): string => {
    const found = shippingTypes.find((t) => t.id === id);
    return found ? found.label : String(id);
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await listShippingRates(sellerCode);
      setRates(data);
    } catch (err: any) {
      message.error("No se pudieron cargar los rates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sellerCode) load();
    listShippingTypes()
      .then(setShippingTypes)
      .catch(() => setShippingTypes([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerCode]);

  const handleToggleEnabled = async (rate: ShippingRate, enabled: boolean) => {
    try {
      await updateShippingRate(sellerCode, rate.id, { enabled });
      message.success(enabled ? "Rate habilitado" : "Rate deshabilitado");
      load();
    } catch {
      message.error("Error al cambiar estado");
    }
  };

  const handleDelete = async (rate: ShippingRate) => {
    try {
      await deleteShippingRate(sellerCode, rate.id);
      message.success("Rate eliminado");
      load();
    } catch {
      message.error("Error al eliminar");
    }
  };

  const columns = [
    { title: "ID", dataIndex: "id", width: 70 },
    {
      title: "Habilitado",
      dataIndex: "enabled",
      width: 110,
      render: (_: any, row: ShippingRate) => (
        <Switch
          checked={row.enabled}
          onChange={(v) => handleToggleEnabled(row, v)}
          size="small"
        />
      ),
    },
    {
      title: "Título",
      dataIndex: "title",
      render: (t: string, row: ShippingRate) => (
        <div>
          <div>{t}</div>
          {row.description ? (
            <div style={{ color: "#919191", fontSize: 12 }}>{row.description}</div>
          ) : null}
        </div>
      ),
    },
    {
      title: "Precio mín.",
      dataIndex: "minPrice",
      width: 110,
      align: "right" as const,
      render: (v: number) => (v != null ? `$ ${Number(v).toLocaleString("es-AR")}` : "-"),
    },
    {
      title: "Cálculo",
      dataIndex: "priceCalculation",
      width: 90,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: "Tipos",
      dataIndex: "shippingTypeIds",
      render: (ids: number[]) =>
        ids?.length ? (
          <Space size={[4, 4]} wrap>
            {ids.map((id) => (
              <Tag key={id}>{shippingTypeLabel(id)}</Tag>
            ))}
          </Space>
        ) : (
          <span style={{ color: "#919191" }}>—</span>
        ),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 120,
      align: "right" as const,
      render: (_: any, row: ShippingRate) => (
        <Space>
          <Tooltip title="Editar">
            <Button
              icon={<EditOutlined />}
              onClick={() => {
                setEditing(row);
                setDrawerOpen(true);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="¿Eliminar rate?"
            description="Esta acción no se puede deshacer."
            okText="Eliminar"
            okButtonProps={{ danger: true }}
            cancelText="Cancelar"
            onConfirm={() => handleDelete(row)}
          >
            <Button icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    // Sin padding propio: el layout ya lo pone, y con el de acá el título quedaba más abajo que en
    // el resto de las páginas (xcons-docs#509).
    <div>
      {/* El ícono del menú, como el resto de los títulos de página. */}
      <Space align="center" style={{ marginBottom: 20 }}>
        <TruckOutlined style={{ fontSize: 22 }} />
        <Title level={3} style={{ margin: 0 }}>
          Configuración de envíos — {sellerCode}
        </Title>
      </Space>

      <DeliveryConfigCard sellerCode={sellerCode} />

      <StorePickupCard sellerCode={sellerCode} />

      <ZippinCard sellerCode={sellerCode} />

      <Card
        title="Shipping rates"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditing(null);
              setDrawerOpen(true);
            }}
          >
            Nuevo rate
          </Button>
        }
      >
        <Spin spinning={loading}>
          <Table
            rowKey="id"
            dataSource={rates}
            columns={columns}
            pagination={{ pageSize: 20, showSizeChanger: false }}
            size="middle"
            locale={{ emptyText: "Sin shipping rates configurados" }}
          />
        </Spin>
      </Card>

      <RateFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSaved={load}
        sellerCode={sellerCode}
        initialRate={editing}
      />
    </div>
  );
};
